import { useEffect, useRef, useState } from "react";
import { LocaleLink } from "@/components/site/LocaleLink";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Reveal } from "@/components/site/Reveal";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { useT } from "@/lib/i18n/I18nProvider";
import { localizeTopic, topicFallback } from "@/content/topic-i18n";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { REPORTS, type Report } from "@/content/reports";
import { UpNextReports, relatedReportsFor, relatedLinkFor } from "@/components/site/RelatedReports";
import { REPORT_DETAILS, type ReportDetails } from "@/content/report-details";
import { REPORT_DETAILS_BG } from "@/content/report-details-bg";
import { ReportCitation } from "@/components/site/ReportCitation";
import { ReportsNewsletter } from "@/components/site/ReportsNewsletter";
import { ReportByline, ReportMethodology, formatReportDate } from "@/components/site/ReportMeta";
import { reportRequestSchema } from "@/lib/reports/schema";
import { toast } from "sonner";
import { track, trackFormFailure, trackFormSubmit } from "@/lib/analytics/track";
import { failureReasonForStatus } from "@/lib/analytics/cta-events";
import { logFormSubmission, hubspotFailReason } from "@/lib/form-log/client";

/** Reports whose PDF is delivered in English only, even from the BG page. */
const ENGLISH_PDF_BG_SLUGS = new Set([
  "industry-spotlight-pharma-in-bulgaria",
  "e-health-age-and-regional-patterns-in-bulgaria",
  "from-energy-shock-to-energy-transition",
]);

type Props = {
  report: Report;
  details: ReportDetails;
};

export function ReportPage({ report, details }: Props) {
  const { locale } = useT();
  const isBg = locale === "bg";
  const bg = isBg ? REPORT_DETAILS_BG[report.slug] : undefined;
  const title = isBg ? report.titleBg ?? report.title : report.title;
  const subtitle = bg?.subtitle ?? details.subtitle;
  const intro = bg?.intro ?? details.intro;
  const takeaways = bg?.takeaways ?? details.takeaways;
  const heroAlt = bg?.heroAlt ?? details.heroImage.alt;
  const heroSrc = bg?.heroImageSrc ?? details.heroImage.src;
  const formPrompt = bg?.formPrompt ?? details.formPrompt;
  const sections = bg?.sections ?? details.sections;
  const keyFindings = isBg ? bg?.keyFindings : details.keyFindings;
  // One template for every report: H1 is the report title, no stat cards, no inline chart.
  const headline = title;
  const showTitleInEyebrow = false;
  const methodology = isBg ? bg?.methodology : details.methodology;
  const chartImage = isBg ? bg?.chartImage : details.chartImage;
  // Summary: at most 3 paragraphs. Methodology text, when present, is the last of them.
  const summaryParas = methodology
    ? [...[...intro, ...(sections ?? []).flatMap((s) => s.paragraphs)].slice(0, 2), methodology]
    : [...intro, ...(sections ?? []).flatMap((s) => s.paragraphs)].slice(0, 3);
  const explicitInside = isBg ? bg?.insideItems : details.insideItems;
  // Fallback: the report's own takeaway headlines (already written per language).
  // Key findings, when present, are what the inside card lists.
  const insideItems =
    keyFindings && keyFindings.length > 0
      ? keyFindings
      : explicitInside && explicitInside.length > 0
      ? explicitInside
      : (takeaways ?? []).map((t) => t.lead.trim().replace(/[\s:.\-\u2013]+$/, "")).filter(Boolean).slice(0, 5);
  const pageCount = isBg ? bg?.pageCount ?? details.pageCount : details.pageCount;
  const chartCount = isBg ? bg?.chartCount ?? details.chartCount : details.chartCount;
  const previewImages = (isBg ? bg?.previewImages ?? details.previewImages : details.previewImages)?.slice(0, 3);
  const sampleSize = isBg ? bg?.sampleSize : details.sampleSize;
  const period = isBg ? bg?.period : details.period;
  const sources = isBg ? bg?.sources : details.sources;
  const analystName = (isBg ? bg?.analystName ?? details.analystName : details.analystName);
  const analystRole = isBg ? bg?.analystRole : details.analystRole;
  const analystPhoto = (isBg ? bg?.analystPhoto ?? details.analystPhoto : details.analystPhoto);
  const publishedDate = (isBg ? bg?.publishedDate ?? details.publishedDate : details.publishedDate);
  const pageUrl = `https://perceptica.com${isBg ? "/bg" : ""}/reports/${report.slug}`;
  // Methodology text moves into the summary; the box stays only for structured-only details.
  const hasMethodology = !methodology && Boolean(sampleSize || period || (sources && sources.length));
  const methodologyBox = hasMethodology ? (
    <ReportMethodology isBg={isBg} sampleSize={sampleSize} period={period} sources={sources} />
  ) : null;

  useEffect(() => {
    const loc = isBg ? "bg" : "en";
    track("report_view", { slug: report.slug, locale: loc });
    const el = document.getElementById("download-form");
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        track("report_scroll_to_form", { slug: report.slug, locale: loc });
        io.disconnect();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [report.slug, isBg]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main">
        {/* Hero */}
        <section className="border-b border-border/60 bg-secondary/30">
          <div className="mx-auto max-w-[1200px] px-6 py-16 lg:px-10 lg:py-24">
            <Reveal>
              <Breadcrumbs
                className="mb-6"
                items={[
                  { label: isBg ? "Начало" : "Home", to: "/" },
                  { label: isBg ? "Доклади" : "Reports", to: "/reports" },
                  { label: title },
                ]}
              />
              <div className="grid items-center gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
                <div>
                  <p className="text-[12.5px] font-medium uppercase tracking-[0.14em] text-accent">
                    {showTitleInEyebrow ? `${title} · ` : ""}
                    {localizeTopic(report.topic, locale) ?? topicFallback(locale)}
                    {report.year ? ` · ${report.year}` : ""}
                  </p>
                  <h1 className="font-display mt-4 text-[clamp(2rem,4vw,3.4rem)] font-medium leading-[1.08] text-foreground">
                    {headline}
                  </h1>
                  {subtitle && (
                    <p className="mt-6 text-[clamp(1.05rem,1.6vw,1.25rem)] font-medium leading-snug text-foreground/80">
                      {subtitle}
                    </p>
                  )}
                  <div className="mt-8">
                    <Button asChild size="lg" className="h-12 px-7 text-[15px]">
                      <a
                        href="#download-form"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById("download-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                      >
                        {isBg ? "Изтеглете пълния доклад" : "Get the full report"}
                      </a>
                    </Button>
                  </div>
                </div>
                {heroSrc ? (
                <figure className="w-full overflow-hidden rounded-2xl border border-border/60 bg-secondary/40 shadow-[var(--shadow-soft)]">
                  <img
                    src={heroSrc}
                    alt={heroAlt}
                    className="h-auto w-full object-contain"
                  />
                  {publishedDate ? (
                    <figcaption className="border-t border-border/60 px-4 py-2.5 text-[12.5px] leading-none text-muted-foreground">
                      {isBg ? "Публикуван " : "Published "}
                      <time dateTime={publishedDate.slice(0, 10)}>{formatReportDate(publishedDate, isBg)}</time>
                    </figcaption>
                  ) : null}
                </figure>
                ) : null}
              </div>
              <ReportByline isBg={isBg} name={analystName} role={analystRole} photo={analystPhoto} date={heroSrc ? undefined : publishedDate} />
            </Reveal>
          </div>
        </section>

        {/* Body */}
        <section className="py-16 lg:py-24">
          <div className="mx-auto max-w-[1200px] px-6 lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-14 lg:px-10">
            <div className="mx-auto max-w-[760px] lg:mx-0 lg:min-w-0">
            <Reveal className="space-y-5 text-[16.5px] leading-relaxed text-foreground/85">
              {summaryParas.map((p) => (
                <p key={p.slice(0, 32)}>{p}</p>
              ))}
            </Reveal>

            {insideItems && insideItems.length > 0 && (
              <section aria-labelledby="inside-heading" className="mt-8 rounded-2xl border border-accent/25 bg-[image:var(--gradient-brand-soft)] p-5 sm:p-6">
                <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-accent-strong">
                  {isBg ? "В PDF файла" : "Inside the PDF"}
                </p>
                <h2 id="inside-heading" className="font-display mt-1 text-[1.25rem] font-medium leading-tight text-foreground">
                  {isBg ? "Какво съдържа пълният доклад" : "What's inside the full report"}
                </h2>
                {(pageCount || chartCount) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {pageCount ? (
                      <span className="rounded-full bg-background px-3.5 py-1.5 text-[13px] font-semibold text-foreground shadow-sm">
                        {pageCount} {isBg ? "страници" : "pages"}
                      </span>
                    ) : null}
                    {chartCount ? (
                      <span className="rounded-full bg-background px-3.5 py-1.5 text-[13px] font-semibold text-foreground shadow-sm">
                        {chartCount} {isBg ? "графики" : "charts"}
                      </span>
                    ) : null}
                  </div>
                )}
                <div className={previewImages && previewImages.length > 0 ? "mt-4 grid gap-6 sm:grid-cols-[1.4fr_1fr] sm:items-center" : "mt-4"}>
                  <ul className="grid gap-2 text-[14.5px] leading-snug text-foreground/90">
                    {insideItems.map((item) => (
                      <li key={item.slice(0, 40)} className="flex gap-2.5">
                        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                          <Check aria-hidden="true" className="h-2.5 w-2.5" />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {previewImages && previewImages.length > 0 && (
                    <div aria-hidden="true" className="relative flex h-36 items-center justify-center">
                      {previewImages.map((src, i) => {
                        const tilt = ["-rotate-6 -translate-x-10", "rotate-0 z-10", "rotate-6 translate-x-10"][previewImages.length === 2 ? i * 2 : i];
                        return (
                          <img
                            key={src}
                            src={src}
                            alt=""
                            loading="lazy"
                            className={`absolute h-32 w-auto rounded-md border border-border/60 bg-background shadow-[var(--shadow-soft)] blur-[1.5px] ${tilt}`}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            )}

            {chartImage && (
              <figure className="mt-12 overflow-hidden rounded-xl border border-border/60">
                <img src={chartImage.src} alt={chartImage.alt} loading="lazy" className="h-auto w-full" />
              </figure>
            )}



            <div className={methodologyBox ? "mt-6 grid gap-6 xl:grid-cols-2 xl:items-start [&>*]:!mt-0" : "mt-6 [&>*]:!mt-0"}>
              {methodologyBox}
              <ReportCitation title={title} year={report.year} url={pageUrl} slug={report.slug} isBg={isBg} />
            </div>



            </div>

            {/* Lead-capture form: sticky right column on desktop, end of page on mobile */}
            <aside className="mt-14 lg:mt-0">
              <div className="lg:sticky lg:top-24">
                <div className="mt-5 scroll-mt-24" id="download-form">
                  <DownloadForm report={report} prompt={formPrompt} isBg={isBg} formHeading={bg?.formHeading} formCta={bg?.formCta} pageCount={pageCount} />
                </div>
                {details.partnerStudy && (
                  <div className="mt-5 flex items-center gap-3 border-t border-border/40 pt-4">
                    {details.partnerStudy.coverImage && (
                      <a href={details.partnerStudy.url} target="_blank" rel="noopener noreferrer" aria-label={details.partnerStudy.name} className="shrink-0">
                        <img
                          src={details.partnerStudy.coverImage}
                          alt=""
                          loading="lazy"
                          className="h-24 w-auto rounded-md object-contain shadow-sm"
                        />
                      </a>
                    )}
                    <p className="min-w-0 text-[12.5px] leading-relaxed text-muted-foreground">
                      {isBg ? "Част от по-голямо изследване: " : "Part of a larger study: "}
                      <a href={details.partnerStudy.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 transition-colors hover:text-foreground">
                        {details.partnerStudy.name}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>
        <MobileReportBar isBg={isBg} />

        <section className="mx-auto max-w-[1200px] px-6 pb-12 lg:px-10 lg:pb-16">
          <ReportsNewsletter isBg={isBg} />
        </section>

        <UpNextReports reports={relatedReportsFor(report, 2)} industryLink={relatedLinkFor(report)} />
      </main>
      <SiteFooter />
    </div>
  );
}

function DownloadForm({ report, prompt, isBg, formHeading, pageCount }: { report: Report; prompt?: string; isBg: boolean; formHeading?: string; formCta?: string; pageCount?: number }) {
  const [emailWarning, setEmailWarning] = useState(false);
  const formStarted = useRef(false);
  const [lead, setLead] = useState<ReportLead | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerError(null);
    setErrors({});
    const fd = new FormData(e.currentTarget);
    const firstName = String(fd.get("firstName") ?? "").trim();
    const lastName = String(fd.get("lastName") ?? "").trim();
    const jobTitle = String(fd.get("jobTitle") ?? "").trim();
    const updatesOptIn = fd.get("updatesOptIn") === "on";
    const payload = {
      name: [firstName, lastName].filter(Boolean).join(" "),
      email: String(fd.get("email") ?? ""),
      company: String(fd.get("company") ?? ""),
      jobTitle,
      reportSlug: report.slug,
      reportTitle: report.title,
      // Processing is covered by the notice under the button (no checkbox).
      consent: true,
      website: String(fd.get("website") ?? ""),
      pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      formName: `report-request:${report.slug}`,
    };
    const fieldErrors: Record<string, string> = {};
    if (!firstName) fieldErrors.firstName = isBg ? "Задължително" : "Required";
    if (!lastName) fieldErrors.lastName = isBg ? "Задължително" : "Required";
    if (!jobTitle) fieldErrors.jobTitle = isBg ? "Задължително" : "Required";
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    const parsed = reportRequestSchema.safeParse(payload);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(flat)) if (v?.[0]) next[k] = v[0];
      setErrors(next);
      // Guard against silent no-op when the failing fields are hidden
      // (formName, pagePath, honeypot). Surface a generic error so the
      // user isn't left staring at an inert button.
      const visibleFields = new Set(["firstName", "lastName", "email", "company", "jobTitle", "consent", "name"]);
      const hasVisibleError = Object.keys(next).some((k) => visibleFields.has(k));
      if (!hasVisibleError) {
        setServerError(isBg ? "Нещо се обърка при валидацията. Опитайте отново." : "Something went wrong while validating the form. Please try again.");
      }
      trackFormFailure({ form_name: payload.formName, reason: "validation_error", error_code: Object.keys(next).join(","), page_path: payload.pagePath, locale: isBg ? "bg" : "en" });
      return;
    }
    setSubmitting(true);
    const backupBase = {
      formType: "report-request",
      formName: payload.formName,
      pagePath: payload.pagePath,
      name: parsed.data.name,
      email: parsed.data.email,
      company: parsed.data.company,
      message: `Requested report: ${parsed.data.reportTitle}`,
      payload: { reportSlug: parsed.data.reportSlug, locale: isBg ? "bg" : "en", jobTitle, updatesOptIn },
    };
    try {
      const hsConfig = HUBSPOT_FORMS[report.slug];
      const hsFormId = hsConfig ? (isBg ? hsConfig.bg ?? null : hsConfig.en) : null;
      const useHubspotForms = !!hsFormId;
      const lead: ReportLead = { email: parsed.data.email, firstName, lastName, company: parsed.data.company, jobTitle, updatesOptIn };
      let res: Response;
      let data: { ok?: boolean; error?: string } = {};
      let hsErrors: { message?: string; errorType?: string }[] = [];
      if (useHubspotForms) {
        const r = await submitReportToHubspot(report.slug, lead, isBg);
        res = r.res;
        data = { ok: r.res.ok };
        hsErrors = r.errors;
      } else {
        res = await fetch("/api/public/report-request", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(parsed.data),
        });
        data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      }
      if (!res.ok || !data.ok) {
        // Map HubSpot field-level errors onto the matching inputs.
        const HS_FIELD_MAP: Record<string, string> = {
          email: "email",
          firstname: "firstName",
          lastname: "lastName",
          company: "company",
          jobtitle: "jobTitle",
        };
        const mapped: Record<string, string> = {};
        const unmapped: { message?: string; errorType?: string }[] = [];
        for (const err of hsErrors) {
          const type = err.errorType ?? "";
          if (type === "INVALID_EMAIL" || type === "BLOCKED_EMAIL") {
            mapped.email = isBg ? "Моля, използвайте валиден служебен имейл" : "Please use a valid work email";
          } else if (type === "REQUIRED_FIELD") {
            const msg = (err.message ?? "").toLowerCase();
            const hsField = Object.keys(HS_FIELD_MAP).find((f) => msg.includes(`'${f}'`) || msg.includes(`fields.${f}`));
            if (hsField) mapped[HS_FIELD_MAP[hsField]] = isBg ? "Това поле е задължително" : "This field is required";
            else unmapped.push(err);
          } else {
            unmapped.push(err);
          }
        }
        const hasFieldErrors = Object.keys(mapped).length > 0;
        if (useHubspotForms) {
          const base = hubspotFailReason(res.status);
          const detail = hsErrors.map((e) => `${e.errorType ?? "UNKNOWN"}: ${e.message ?? ""}`).join(" | ");
          void logFormSubmission({
            ...backupBase,
            hubspotOk: false,
            hubspotReason: (detail ? `${base} ${detail}` : base).slice(0, 300),
          });
        }
        if (hasFieldErrors) setErrors(mapped);
        if (hasFieldErrors && unmapped.length === 0) {
          // Field messages already explain the problem; no generic banner.
        } else if (res.status === 429) {
          setServerError(isBg ? "Прекалено много заявки в момента. Опитайте отново след минута." : "Too many requests right now. Please try again in a minute.");
        } else if (res.status >= 500) {
          setServerError(
            isBg
              ? "Не успяхме да обработим заявката ви - възникна сървърна грешка. Опитайте отново или ни пишете на sales@perceptica.com."
              : "We couldn't submit your request - our server hit an error. Please try again, or email sales@perceptica.com.",
          );
        } else {
          setServerError(data.error ?? (isBg ? "Нещо се обърка. Опитайте отново." : "Something went wrong. Please try again."));
        }
        toast.error(isBg ? "Не успяхме да изпратим заявката." : "We couldn't submit your request.");
        trackFormFailure({ form_name: payload.formName, reason: failureReasonForStatus(res.status), error_code: hsErrors[0]?.errorType ?? res.status, page_path: payload.pagePath, locale: isBg ? "bg" : "en" });
        return;
      }
      saveLead(lead);
      setLead(lead);
      if (useHubspotForms) void logFormSubmission({ ...backupBase, hubspotOk: true });
      toast.success(isBg ? "Заявката е изпратена - ще получите доклада скоро." : "Request sent - you'll receive the report shortly.");
      trackFormSubmit("submit_report_request", { form_name: payload.formName, page_path: payload.pagePath, locale: isBg ? "bg" : "en" });
      track("report_form_submit", { slug: report.slug, locale: isBg ? "bg" : "en", updates_optin: updatesOptIn });
    } catch {
      void logFormSubmission({ ...backupBase, hubspotOk: false, hubspotReason: hubspotFailReason(null) });
      setServerError(isBg ? "Мрежова грешка - проверете връзката си и опитайте отново." : "Network error - please check your connection and try again.");
      trackFormFailure({ form_name: payload.formName, reason: "network_error", page_path: payload.pagePath, locale: isBg ? "bg" : "en" });
      toast.error(isBg ? "Мрежова грешка." : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  const microcopy = isBg
    ? `PDF · ${pageCount ? `${pageCount} страници · ` : ""}пристига до минута`
    : `PDF · ${pageCount ? `${pageCount} pages · ` : ""}arrives within a minute`;

  if (lead) return <ReportThankYou report={report} lead={lead} isBg={isBg} />;

  return (
    <div className="rounded-2xl border border-border/60 bg-background p-6 shadow-[var(--shadow-soft)] lg:p-7">
      <p className="text-[15px] font-semibold text-foreground">
        {prompt ?? (isBg ? "Получете целия доклад като PDF." : "Get the complete report as a PDF.")}
      </p>
      <h3 className="font-display mt-1.5 text-[1.3rem] font-medium text-foreground">
        {isBg ? formHeading ?? "Изтегли доклада" : "Download the report"}
      </h3>
      {isBg && ENGLISH_PDF_BG_SLUGS.has(report.slug) && (
        <p className="mt-2 text-[12.5px] text-muted-foreground">
          Моля, имайте предвид, че този доклад се доставя като PDF файл на английски език.
        </p>
      )}

      {(
        <form name="report-download" id="report-download" data-hs-do-not-collect="true" className="mt-5 space-y-3" onSubmit={handleSubmit} noValidate onFocus={() => {
          if (formStarted.current) return;
          formStarted.current = true;
          track("report_form_start", { slug: report.slug, locale: isBg ? "bg" : "en" });
        }}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={isBg ? "Име" : "First name"} name="firstName" autoComplete="given-name" required error={errors.firstName} />
            <Field label={isBg ? "Фамилия" : "Last name"} name="lastName" autoComplete="family-name" required error={errors.lastName} />
          </div>
          <Field
            label={isBg ? "Служебен имейл" : "Work email"}
            name="email"
            type="email"
            autoComplete="email"
            required
            error={errors.email}
            hint={!errors.email && emailWarning ? (isBg ? "Изглежда като личен имейл. Служебният ни помага да ви изпратим подходящите доклади." : "This looks like a personal email. A work email helps us send you the right reports.") : undefined}
            onBlur={(e) => setEmailWarning(isFreeEmail(e.currentTarget.value))}
            onChange={(e) => { if (emailWarning) setEmailWarning(isFreeEmail(e.currentTarget.value)); }}
          />
          <Field label={isBg ? "Компания" : "Company"} name="company" autoComplete="organization" required error={errors.company} />
          <Field label={isBg ? "Длъжност" : "Job title"} name="jobTitle" autoComplete="organization-title" list={`job-titles-${isBg ? "bg" : "en"}`} required error={errors.jobTitle} />
          <datalist id={`job-titles-${isBg ? "bg" : "en"}`}>
            {(isBg ? JOB_TITLES_BG : JOB_TITLES_EN).map((t) => <option key={t} value={t} />)}
          </datalist>

          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute h-0 w-0 overflow-hidden opacity-0"
          />

          <label className="flex items-start gap-2 pt-1 text-[12.5px] text-foreground/75">
            <input
              type="checkbox"
              name="updatesOptIn"
              className="mt-0.5 h-4 w-4 rounded border-border accent-foreground"
            />
            <span>{isBg ? UPDATES_TEXT_BG : UPDATES_TEXT_EN}</span>
          </label>

          {serverError && (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-md border border-destructive/30 bg-destructive/5 p-3"
            >
              <p className="text-[12.5px] font-medium text-destructive">
                {isBg ? "Не успяхме да изпратим заявката ви" : "We couldn't submit your request"}
              </p>
              <p className="mt-1 text-[12.5px] text-destructive/90">{serverError}</p>
            </div>
          )}

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            {submitting ? (isBg ? "Изпраща…" : "Sending…") : isBg ? "Изпратете ми доклада" : "Email me the full report"}
          </Button>
          <p className="text-center text-[12px] text-muted-foreground">{microcopy}</p>
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            {isBg ? "Използваме данните ви, за да ви изпратим доклада. Вижте " : "We use your details to send you this report. See our "}
            <LocaleLink to="/privacy-policy" className="underline underline-offset-2">
              {isBg ? "политиката ни за поверителност" : "privacy policy"}
            </LocaleLink>
            .
          </p>
        </form>
      )}
    </div>
  );
}

// Per-report HubSpot Forms API submissions (triggers automation/email).
// Map: slug -> { en: formId, bg?: formId }. If a locale-specific form
// isn't configured, falls back to the default /api/public/report-request.
const HUBSPOT_FORMS: Record<string, { en: string; bg?: string; region?: "na1" | "eu1" }> = {
  "ai-in-public-discourse": {
    en: "36be7aa5-d305-4a95-b1ec-96bf6828e9ca",
    bg: "8cddc0a4-be7d-410c-b35d-a486575d715b",
    region: "eu1",
  },
  "bulgarian-banking-and-the-euro-transition": {
    en: "757359b0-862d-48fe-aa53-898d9ff73c33",
    bg: "c32f8b5e-cd75-419c-9f2c-8c7d7325b82f",
    region: "eu1",
  },
  "renewable-energy-in-the-southeast-europe-region": {
    en: "4c0599da-5388-4bba-a967-3323e053d469",
    bg: "9f76ec4d-47e0-4261-a605-486f90e26fba",
    region: "eu1",
  },
  "the-most-media-visible-founders-ventures-in-bulgaria": {
    en: "645e92b8-01bb-4fd8-9688-691fc09a9eca",
    bg: "a77e23a6-0465-461b-b88f-61c0a08b942a",
    region: "eu1",
  },
  "the-boycott-of-retail-chains-in-bulgaria": {
    en: "4ebbaa99-b77d-497b-a2c9-bd7956e666f6",
    bg: "c60ad418-faa3-4feb-8f15-cc6d1c8ac978",
    region: "eu1",
  },
  "competitive-analysis-pharma-industry-2023-pharmaceuticals": {
    en: "c6dcc9e4-6a31-43ef-9c34-ff8584129971",
    bg: "f0a24e83-0e7b-43e4-85a4-652b1034878a",
    region: "eu1",
  },
  "competitive-analysis-of-the-pharma-industry-2023-supplements-vol-2": {
    en: "d047193e-984a-4a22-8e76-49edc8fa1321",
    bg: "bc6b0903-5e96-4c0d-af18-a79fd8a92aef",
    region: "eu1",
  },
  "bulgarian-banking-industry-report": {
    en: "9a147e53-9f10-4de8-8c06-173c9a0b5b8d",
    bg: "8cbb3229-25d8-40d8-ab8a-57b783f5aa03",
    region: "eu1",
  },
  "analysis-supermarket-industry-in-bulgaria": {
    en: "2d44b359-9249-4975-81e1-70c7d2cf2941",
    bg: "e6c9f36d-1679-4fef-afc4-d178e265ecaf",
    region: "eu1",
  },
  "media-in-southeast-europe-common-narratives-in-nato-members": {
    en: "1050bb03-24b5-46cc-a224-ae88e8fe76b2",
    bg: "f3119e96-9579-4002-97ab-40e895535fe3",
    region: "eu1",
  },
  "communications-challenges-in-the-retail-sector": {
    en: "8e2cb42a-2c3c-45e6-8ee6-543e995090e5",
    bg: "5889cf8a-542f-4f14-9606-5c57b571bf9c",
    region: "eu1",
  },
  "communications-challenges-in-the-banking-industry": {
    en: "9a9511dd-9897-4efc-af6b-771390b4bf0d",
    bg: "1ccbd56c-f599-4c3d-bc03-b1fb1300241b",
    region: "eu1",
  },
  "communications-challenges-in-the-pharma-industry": {
    en: "97879279-a467-4db6-94ac-fa7b592a857a",
    bg: "c212b991-032a-4443-99da-9a45ad5fac78",
    region: "eu1",
  },
  "back-to-school-2026": {
    en: "60f51fe6-dc08-4365-821c-c6aec9cab52b",
    bg: "d623bfaa-e00b-4d71-a638-85dd4e818c97",
    region: "eu1",
  },
  "industry-spotlight-banking-in-bulgaria": {
    en: "54b4f1df-d860-43a9-9624-44004b8b676d",
    bg: "858d2c0b-ea13-43ea-9dfc-1562959a0bd2",
    region: "eu1",
  },
  "industry-spotlight-pharma-in-bulgaria": {
    en: "9e1f033a-6f00-4574-be2a-ab7b92e889eb",
    bg: "77c2d8d9-69c6-4611-9c04-6b299903188a",
    region: "eu1",
  },
  "e-health-age-and-regional-patterns-in-bulgaria": {
    en: "ae2464ff-dbad-4647-ad0f-ed296fe4f36f",
    bg: "9c0f01cc-81bb-4f46-82fe-156fb23f0afd",
    region: "eu1",
  },
  "from-energy-shock-to-energy-transition": {
    en: "779b4aad-3785-4c15-b258-20225b2ee459",
    bg: "7d4ce8e6-c668-4271-b8a7-7bdc9bc76c19",
    region: "eu1",
  },
};

type ReportLead = { email: string; firstName: string; lastName: string; company: string; jobTitle: string; updatesOptIn: boolean };
type HsError = { message?: string; errorType?: string };
const LEAD_KEY = "perceptica:report-lead";
function saveLead(lead: ReportLead) {
  try {
    sessionStorage.setItem(LEAD_KEY, JSON.stringify(lead));
  } catch {
    /* storage unavailable */
  }
}
function readHutk(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const raw = document.cookie.split("; ").find((c) => c.startsWith("hubspotutk="))?.slice("hubspotutk=".length);
  return raw ? decodeURIComponent(raw) : undefined;
}
function hsContext() {
  const hutk = readHutk();
  return {
    ...(hutk ? { hutk } : {}),
    pageUri: typeof window !== "undefined" ? window.location.href : "",
    pageName: typeof document !== "undefined" ? document.title : "",
  };
}
/**
 * Submits a lead to a report's own HubSpot form (eu1 unless na1 is explicit),
 * so that form's delivery workflow fires. Throws on network failure.
 */
async function submitReportToHubspot(slug: string, lead: ReportLead, isBg: boolean): Promise<{ res: Response; errors: HsError[] }> {
  const cfg = HUBSPOT_FORMS[slug];
  const formId = cfg ? (isBg ? cfg.bg ?? cfg.en : cfg.en) : null;
  if (!cfg || !formId) throw new Error("no_form");
  const host = cfg.region === "na1" ? "api.hsforms.com" : "api-eu1.hsforms.com";
  const res = await fetch(`https://${host}/submissions/v3/integration/submit/25788344/${formId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fields: [
        { objectTypeId: "0-1", name: "email", value: lead.email },
        { objectTypeId: "0-1", name: "firstname", value: lead.firstName },
        { objectTypeId: "0-1", name: "lastname", value: lead.lastName },
        { objectTypeId: "0-1", name: "company", value: lead.company },
        { objectTypeId: "0-1", name: "jobtitle", value: lead.jobTitle },
      ],
      context: hsContext(),
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text: isBg ? PROCESSING_TEXT_BG : PROCESSING_TEXT_EN,
          ...(HUBSPOT_UPDATES_SUBSCRIPTION_TYPE_ID !== null
            ? {
                communications: [
                  {
                    value: lead.updatesOptIn,
                    subscriptionTypeId: HUBSPOT_UPDATES_SUBSCRIPTION_TYPE_ID,
                    text: isBg ? UPDATES_TEXT_BG : UPDATES_TEXT_EN,
                  },
                ],
              }
            : {}),
        },
      },
    }),
  });
  let errors: HsError[] = [];
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { errors?: HsError[] } | null;
    errors = Array.isArray(body?.errors) ? body!.errors : [];
  }
  return { res, errors };
}

const PROCESSING_TEXT_EN = "We use your details to send you this report. See our privacy policy.";
const PROCESSING_TEXT_BG = "Използваме данните ви, за да ви изпратим доклада. Вижте политиката ни за поверителност.";
const UPDATES_TEXT_EN = "Send me new Perceptica reports and occasional insights.";
const UPDATES_TEXT_BG = "Искам да получавам новите доклади на Perceptica и подбрани анализи.";
/**
 * HubSpot subscription type for the optional "new reports" opt-in.
 * Set to the numeric ID from HubSpot (Settings > Marketing > Email > Subscription types).
 * While null, the communications entry is omitted, because HubSpot rejects
 * submissions that reference an unknown subscription type.
 */
const HUBSPOT_UPDATES_SUBSCRIPTION_TYPE_ID: number | null = null;
const JOB_TITLES_EN = ["PR Manager", "Communications Manager", "Brand Manager", "Marketing Manager", "Head of Communications", "Agency Account Director", "Analyst"];
const JOB_TITLES_BG = ["PR мениджър", "Мениджър комуникации", "Бранд мениджър", "Маркетинг мениджър", "Директор комуникации", "Акаунт директор в агенция", "Анализатор"];
const FREE_EMAIL_DOMAINS = new Set(["gmail.com", "yahoo.com", "abv.bg", "mail.bg", "outlook.com", "hotmail.com"]);
function isFreeEmail(v: string): boolean {
  const domain = v.trim().toLowerCase().split("@")[1];
  return !!domain && FREE_EMAIL_DOMAINS.has(domain);
}

const NEWSLETTER_FORMS = { en: "e12d3da2-3c21-4096-99cd-64a4cc18a27f", bg: "930ef5bf-9ed4-4625-a4b1-8aadb2027265" };

function nextReportFor(slug: string): Report | undefined {
  // REPORTS is ordered newest first; pick the newest other report with its own HubSpot form.
  return REPORTS.find((r) => r.slug !== slug && r.slug in HUBSPOT_FORMS && r.slug in REPORT_DETAILS);
}

type SendState = "idle" | "sending" | "sent" | "error";

function ReportThankYou({ report, lead, isBg }: { report: Report; lead: ReportLead; isBg: boolean }) {
  const locale = isBg ? "bg" : "en";
  const pagePath = typeof window !== "undefined" ? window.location.pathname : "";
  const next = nextReportFor(report.slug);
  const nextDetails = next ? REPORT_DETAILS[next.slug] : undefined;
  const nextBg = next && isBg ? REPORT_DETAILS_BG[next.slug] : undefined;
  const nextTitle = next ? (isBg ? next.titleBg ?? next.title : next.title) : "";
  const nextStat = (isBg ? nextBg?.heroStats : nextDetails?.heroStats)?.[0];
  const nextCover = nextBg?.heroImageSrc ?? nextDetails?.heroImage.src;
  const partnerStudy = REPORT_DETAILS[report.slug]?.partnerStudy;
  const [nextState, setNextState] = useState<SendState>("idle");
  const [subState, setSubState] = useState<SendState>("idle");

  async function sendNext() {
    if (!next || nextState === "sending" || nextState === "sent") return;
    setNextState("sending");
    track("report_next_read_click", { slug: report.slug, next_slug: next.slug, locale });
    const formName = `report-request:${next.slug}`;
    const backup = {
      formType: "report-request",
      formName,
      pagePath,
      name: `${lead.firstName} ${lead.lastName}`.trim(),
      email: lead.email,
      company: lead.company,
      message: `Requested report: ${next.title} (next read after ${report.slug})`,
      payload: { reportSlug: next.slug, locale, jobTitle: lead.jobTitle, updatesOptIn: lead.updatesOptIn, source: "thank-you-next-read" },
    };
    try {
      const { res, errors } = await submitReportToHubspot(next.slug, lead, isBg);
      if (!res.ok) {
        const detail = errors.map((e) => `${e.errorType ?? "UNKNOWN"}: ${e.message ?? ""}`).join(" | ");
        void logFormSubmission({ ...backup, hubspotOk: false, hubspotReason: `${hubspotFailReason(res.status)} ${detail}`.trim().slice(0, 300) });
        trackFormFailure({ form_name: formName, reason: failureReasonForStatus(res.status), error_code: errors[0]?.errorType ?? res.status, page_path: pagePath, locale });
        setNextState("error");
        return;
      }
      void logFormSubmission({ ...backup, hubspotOk: true });
      trackFormSubmit("submit_report_request", { form_name: formName, page_path: pagePath, locale });
      setNextState("sent");
    } catch {
      void logFormSubmission({ ...backup, hubspotOk: false, hubspotReason: hubspotFailReason(null) });
      trackFormFailure({ form_name: formName, reason: "network_error", page_path: pagePath, locale });
      setNextState("error");
    }
  }

  async function subscribe() {
    if (subState === "sending" || subState === "sent") return;
    setSubState("sending");
    track("report_subscribe_click", { report_slug: report.slug, locale });
    const backup = { formType: "reports-notify", formName: "reports-notify-me", pagePath, email: lead.email, payload: { locale, source: "report-thank-you", updatesOptIn: true } };
    try {
      const res = await fetch(`https://api-eu1.hsforms.com/submissions/v3/integration/submit/25788344/${NEWSLETTER_FORMS[locale]}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fields: [{ objectTypeId: "0-1", name: "email", value: lead.email }],
          context: hsContext(),
          legalConsentOptions: {
            consent: {
              consentToProcess: true,
              text: isBg ? PROCESSING_TEXT_BG : PROCESSING_TEXT_EN,
              ...(HUBSPOT_UPDATES_SUBSCRIPTION_TYPE_ID !== null
                ? { communications: [{ value: true, subscriptionTypeId: HUBSPOT_UPDATES_SUBSCRIPTION_TYPE_ID, text: isBg ? UPDATES_TEXT_BG : UPDATES_TEXT_EN }] }
                : {}),
            },
          },
        }),
      });
      void logFormSubmission({ ...backup, hubspotOk: res.ok, ...(res.ok ? {} : { hubspotReason: hubspotFailReason(res.status) }) });
      setSubState(res.ok ? "sent" : "error");
    } catch {
      void logFormSubmission({ ...backup, hubspotOk: false, hubspotReason: hubspotFailReason(null) });
      setSubState("error");
    }
  }

  return (
    <div className="space-y-4" aria-live="polite">
      <div className="rounded-2xl border border-border/60 bg-background p-6 shadow-[var(--shadow-soft)] lg:p-7">
        <h3 className="font-display text-[1.35rem] font-medium leading-tight text-foreground">
          {isBg ? "Докладът е на път. Проверете пощата си." : "On its way. Check your inbox."}
        </h3>
        <p className="mt-2 break-all text-[13.5px] text-muted-foreground">{lead.email}</p>
      </div>

      <div className="rounded-2xl border border-accent/30 bg-secondary/40 p-6">
        <p className="font-display text-[1.1rem] font-medium leading-snug text-foreground">
          {isBg ? "Как изглежда вашият бранд в тази тема?" : "See how your brand appears in this story."}
        </p>
        <p className="mt-2 text-[13.5px] leading-relaxed text-foreground/75">
          {isBg ? "Нашите анализатори ще ви изготвят безплатен експресен анализ на медийното ви присъствие." : "Get a free media snapshot from our analysts."}
        </p>
        <Button asChild size="lg" className="mt-4 w-full">
          <LocaleLink
            to="/request-a-demo"
            search={{ source: "report-snapshot", report: report.slug }}
            onClick={() => track("report_snapshot_click", { slug: report.slug, locale })}
          >
            {isBg ? "Заявете безплатен анализ" : "Request my snapshot"}
          </LocaleLink>
        </Button>
      </div>

      {partnerStudy && (
        <div className="rounded-2xl border border-border/60 bg-background p-6">
          <p className="text-[11.5px] font-semibold uppercase tracking-wider text-accent">{isBg ? "Част от по-голямо изследване" : "Part of a larger study"}</p>
          <div className="mt-3 flex gap-4">
            {partnerStudy.coverImage && <img src={partnerStudy.coverImage} alt="" loading="lazy" className="h-24 w-auto shrink-0 rounded-md border border-border/60 object-contain" />}
            <p className="min-w-0 font-medium leading-snug text-foreground">{partnerStudy.name}</p>
          </div>
          <Button asChild variant="outline" className="mt-4 w-full">
            <a
              href={partnerStudy.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("report_partner_study_click", { report_slug: report.slug, locale })}
            >
              {isBg ? "Разгледайте цялото изследване" : "Explore the full study"}
            </a>
          </Button>
        </div>
      )}

      {next && (
        <div className="rounded-2xl border border-border/60 bg-background p-6">
          <p className="text-[11.5px] font-semibold uppercase tracking-wider text-accent">{isBg ? "Прочетете още" : "Next read"}</p>
          <div className="mt-3 flex gap-4">
            {nextCover && <img src={nextCover} alt="" loading="lazy" className="h-20 w-16 shrink-0 rounded-md border border-border/60 object-cover" />}
            <div className="min-w-0">
              <LocaleLink to={`/reports/${next.slug}`} className="font-medium leading-snug text-foreground hover:text-accent">{nextTitle}</LocaleLink>
              {nextStat && (
                <p className="mt-1.5 text-[13px] text-foreground/75">
                  <strong className="text-foreground">{nextStat.value}</strong> {nextStat.label}
                </p>
              )}
            </div>
          </div>
          {nextState === "sent" ? (
            <p role="status" className="mt-4 text-[13px] font-medium text-foreground">{isBg ? "Изпратено. Ще го получите до минута." : "Sent. It will arrive within a minute."}</p>
          ) : (
            <>
              <Button variant="outline" className="mt-4 w-full" onClick={sendNext} disabled={nextState === "sending"}>
                {nextState === "sending" ? (isBg ? "Изпраща…" : "Sending…") : isBg ? "Изпратете ми и този доклад" : "Send me this one too"}
              </Button>
              {nextState === "error" && (
                <p role="alert" className="mt-2 text-[12.5px] text-destructive">{isBg ? "Не успяхме да изпратим заявката. Опитайте отново." : "We couldn't send this one. Please try again."}</p>
              )}
            </>
          )}
        </div>
      )}

      {!lead.updatesOptIn && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-background px-6 py-4">
          <p className="text-[13.5px] font-medium text-foreground">{isBg ? "Получавайте новите доклади първи" : "Get new reports first"}</p>
          {subState === "sent" ? (
            <Check aria-label={isBg ? "Абонирани" : "Subscribed"} className="h-5 w-5 text-accent" />
          ) : (
            <Button size="sm" variant="outline" onClick={subscribe} disabled={subState === "sending"}>
              {isBg ? "Абонирайте ме" : "Subscribe me"}
            </Button>
          )}
          {subState === "error" && (
            <p role="alert" className="w-full text-[12.5px] text-destructive">{isBg ? "Не успяхме да изпратим заявката. Опитайте отново." : "We couldn't send this one. Please try again."}</p>
          )}
        </div>
      )}
    </div>
  );
}


function MobileReportBar({ isBg }: { isBg: boolean }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const form = document.getElementById("download-form");
    if (!form || typeof IntersectionObserver === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    let formVisible = false;
    let scrolled = false;
    const update = () => setShow(!mq.matches && !formVisible && scrolled);
    const io = new IntersectionObserver(([entry]) => { formVisible = entry.isIntersecting; update(); });
    io.observe(form);
    const onScroll = () => { scrolled = window.scrollY > 400; update(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    mq.addEventListener("change", update);
    onScroll();
    return () => { io.disconnect(); window.removeEventListener("scroll", onScroll); mq.removeEventListener("change", update); };
  }, []);
  if (!show) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 p-3 backdrop-blur lg:hidden">
      <Button
        size="lg"
        className="w-full"
        onClick={() => document.getElementById("download-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
      >
        {isBg ? "Вземете доклада" : "Get the full report"}
      </Button>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  error,
  hint,
  autoComplete,
  list,
  onBlur,
  onChange,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  autoComplete?: string;
  list?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}) {
  const className =
    "w-full rounded-md border bg-background px-3 py-2 text-[13.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 " +
    (error
      ? "border-destructive/60 focus:border-destructive focus:ring-destructive/20"
      : "border-border/60 focus:border-accent focus:ring-accent/20");
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-foreground/80">
        {label}
        {required && <span className="text-accent"> *</span>}
      </span>
      <input name={name} type={type} required={required} autoComplete={autoComplete} list={list} onBlur={onBlur} onChange={onChange} aria-invalid={!!error} className={className} />
      {error && <span className="mt-1 block text-[11.5px] text-destructive">{error}</span>}
      {hint && <span role="status" className="mt-1 block text-[11.5px] text-muted-foreground">{hint}</span>}
    </label>
  );
}
